import { css } from '@emotion/core'
import { ContentRemove, NoticeDoNot } from '@fs/zion-icon/dist/cjs/icons'
import {
  ActionChip,
  Button,
  Cell,
  Checkbox,
  colors,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  FlowGrid,
  Grid,
  Row,
  useOverlay,
  H5,
} from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RawData } from '../../common/RawData'
import { actions } from './useUserContributions'
import getTypeFiltersInitialState from '../../common/TypeFiltersState'

const clickable = css`
  cursor: pointer;
`
export default function UserContributionsTypeFilters({
  userId,
  getContributions,
  typeFiltersState,
  setTypeFiltersState,
}) {
  const overlay = useOverlay()
  const { t } = useTranslation()

  function checkboxOnChange(dropdownKey, accordionKey, event) {
    event.stopPropagation()
    const accordionItems = typeFiltersState[dropdownKey].accordionItems
    if (dropdownKey === 'contributorId' || dropdownKey === 'conclusionType') {
      Object.keys(accordionItems).forEach((accordionkey) => {
        const accordionItem = accordionItems[accordionkey]
        accordionItem.isChecked = false
      })
    }
    accordionItems[accordionKey].isChecked = event.target.checked
    setTypeFiltersState(typeFiltersState)
  }

  const applyFiltersButtonOnClick = async () => {
    const filters = {
      type: '',
      subtypes: [],
    }
    Object.keys(typeFiltersState)
      .filter((dropdownKey) => dropdownKey !== 'currentFilters')
      .forEach((dropdownKey) => {
        const dropdownItem = typeFiltersState[dropdownKey]
        if (dropdownItem.isOpen) {
          filters.type = dropdownKey
          Object.keys(dropdownItem.accordionItems).forEach((accordionKey) => {
            const accordionItem = dropdownItem.accordionItems[accordionKey]
            if (accordionItem.isChecked) {
              filters.subtypes.push(accordionItem.value)
            }
          })
        }
      })
    typeFiltersState.currentFilters = filters
    typeFiltersState.currentFilters.subtypes = typeFiltersState.currentFilters.subtypes.map((subtype) => {
      return { subtype, label: subtype }
    })
    setTypeFiltersState(typeFiltersState)
    userId &&
      getContributions(
        userId,
        null,
        null,
        typeFiltersState.currentFilters.subtypes.length > 0 ? filters.type : null,
        typeFiltersState.currentFilters.subtypes.map((subtype) => subtype.subtype),
        actions.SET_CONTRIBUTIONS
      )
    overlay.close()
  }

  const clearFiltersButtonOnClick = () => {
    setTypeFiltersState(getTypeFiltersInitialState({ t }))
    userId && getContributions(userId, null, null, null, null, actions.SET_CONTRIBUTIONS)
    overlay.close()
  }

  const onFilterChipClick = (subtype) => {
    typeFiltersState.currentFilters.subtypes = typeFiltersState.currentFilters.subtypes.filter(
      (currentFiltersSubType) => currentFiltersSubType.subtype !== subtype
    )
    const itemToDeselect = Object.values(typeFiltersState[typeFiltersState.currentFilters.type].accordionItems).find(
      (item) => item.value === subtype
    )
    itemToDeselect.isChecked = false
    if (typeFiltersState.currentFilters.subtypes.length > 0) {
      userId &&
        getContributions(
          userId,
          null,
          null,
          typeFiltersState.currentFilters.type,
          typeFiltersState.currentFilters.subtypes.map((filterSubtype) => filterSubtype.subtype),
          actions.SET_CONTRIBUTIONS
        )
    } else {
      typeFiltersState.currentFilters.type = ''
      userId && getContributions(userId, null, null, null, null, actions.SET_CONTRIBUTIONS)
    }
    setTypeFiltersState(typeFiltersState)
  }

  function expansionPanelOnClick(currentDropdownItem) {
    Object.keys(typeFiltersState)
      .filter((dropdownKey) => dropdownKey !== 'currentFilters')
      .forEach((dropdownKey) => {
        const dropdownItem = typeFiltersState[dropdownKey]
        if (dropdownItem.label === currentDropdownItem.label) {
          dropdownItem.isOpen = !currentDropdownItem.isOpen // toggle
        } else {
          // clean the rest
          dropdownItem.isOpen = false
          Object.keys(dropdownItem.accordionItems).forEach((accordionKey) => {
            const accordionItem = dropdownItem.accordionItems[accordionKey]
            accordionItem.isChecked = false
          })
        }
      })
    setTypeFiltersState(typeFiltersState)
  }

  return (
    <div>
      <Row alignY="middle">
        <Button id="filtersBtn" emphasis="medium" onClick={overlay.handleClick} noShrinkX>
          {t('filters.label', 'Filters')}
        </Button>
        <Row wrap gutters="nano">
          {typeFiltersState.currentFilters.subtypes.map((subType) => (
            <ActionChip
              id={`${subType.label}_chip`}
              key={subType.label}
              Icon={ContentRemove}
              label={subType.label}
              onClick={() => onFilterChipClick(subType.subtype)}
            />
          ))}
        </Row>
      </Row>
      <DialogOverlay title="" {...overlay} size="xl">
        <Grid>
          {/* Row */}
          <Cell columns={12}>
            {Object.keys(typeFiltersState)
              .filter((dropdownKey) => dropdownKey !== 'currentFilters')
              .map((dropdownKey) => {
                const dropdownItem = typeFiltersState[dropdownKey]
                return (
                  <div key={dropdownKey} data-testid={`expander-${dropdownItem.label}`}>
                    <div css={clickable}>
                      <H5
                        data-testid={`button-${dropdownItem.label}`}
                        onClick={() => expansionPanelOnClick(dropdownItem)}
                      >
                        {dropdownItem.label}
                      </H5>
                    </div>
                    {dropdownItem.isOpen && (
                      <div>
                        <FlowGrid columnCount={3} orientation="vertical" gutters="md">
                          {Object.keys(dropdownItem.accordionItems).map((accordionKey) => {
                            const accordionItem = dropdownItem.accordionItems[accordionKey]
                            return (
                              <React.Fragment key={accordionItem.label}>
                                <Checkbox
                                  id={accordionItem.value}
                                  name="thisIsNotUsedButZionWantsToHaveOne"
                                  label={
                                    <>
                                      {accordionItem.accountIsEnabled === false && (
                                        <>
                                          <NoticeDoNot color={colors.red50} data-testid="disabledAccountIcon" />
                                          {` `}
                                        </>
                                      )}
                                      {accordionItem.label}
                                    </>
                                  }
                                  checked={accordionItem.isChecked}
                                  color="primary"
                                  onChange={(event) => checkboxOnChange(dropdownKey, accordionKey, event)}
                                />
                              </React.Fragment>
                            )
                          })}
                        </FlowGrid>
                      </div>
                    )}
                  </div>
                )
              })}
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            <RawData state={typeFiltersState} />
          </Cell>
        </Grid>

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button id="applyFiltersBtn" onClick={applyFiltersButtonOnClick} emphasis="high">
              {t('user.buttons.applyFilters', 'Apply Filters')}
            </Button>
            <Button id="clearFiltersBtn" onClick={clearFiltersButtonOnClick} emphasis="medium">
              {(t('filters.clear'), 'Clear Filters')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </div>
  )
}
